"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { isValidElement } from "react";
import { classNames, responsiveVariables } from "../../utilities/props.js";
import { resolveMixin } from "../../styles/mixin.js";
import s from "./Table.module.css";
import useFadeSide from "../../hooks/_private/useFadeSide.js";
const TableCellPrivate = (props) => {
    const { minWidth, rowSpan, colSpan, align, verticalAlign, tagName: TagName, padding, paddingInline, paddingBlock, children, className, attributes, } = props;
    const width = props.width === "auto" ? "0px" : props.width;
    const mixinStyles = resolveMixin({ width, minWidth });
    const headingClassNames = classNames(s.cell, mixinStyles.classNames, (width === 0 || width === "0px") && s["cell--width-auto"], align && s[`cell--align-${align}`], verticalAlign && s[`cell--valign-${verticalAlign}`], className);
    const headingStyle = {
        ...mixinStyles.variables,
        ...responsiveVariables("--rs-table-p-vertical", paddingBlock ?? padding),
        ...responsiveVariables("--rs-table-p-horizontal", paddingInline ?? padding),
    };
    return (_jsx(TagName, { ...attributes, className: headingClassNames, rowSpan: rowSpan, colSpan: colSpan, style: headingStyle, children: children }));
};
const TableCell = (props) => {
    return _jsx(TableCellPrivate, { ...props, tagName: "td" });
};
const TableHeading = (props) => {
    return _jsx(TableCellPrivate, { ...props, tagName: "th" });
};
const TableRow = (props) => {
    const { highlighted, children, className, attributes } = props;
    const onClick = props.onClick || attributes?.onClick;
    const rowClassNames = classNames(s.row, highlighted && s["--row-highlighted"], className);
    return (_jsx("tr", { ...attributes, className: rowClassNames, onClick: onClick, tabIndex: onClick ? 0 : undefined, children: children }));
};
const TableBody = (props) => {
    const { children, attributes, className } = props;
    return (_jsx("tbody", { ...attributes, className: classNames(s.body, className), children: children }));
};
const TableHead = (props) => {
    const { children, attributes, className } = props;
    return (_jsx("thead", { ...attributes, className: classNames(s.head, className), children: children }));
};
const Table = (props) => {
    const { children, border, columnBorder, className, attributes } = props;
    const rootRef = React.useRef(null);
    const fadeSide = useFadeSide(rootRef);
    const rootClassNames = classNames(s.root, className, border && s["--border-outer"], columnBorder && s["--border-column"], (fadeSide === "start" || fadeSide === "both") && s["--fade-start"], (fadeSide === "end" || fadeSide === "both") && s["--fade-end"]);
    const [firstChild] = React.Children.toArray(children);
    const isElement = isValidElement(firstChild);
    const isBody = isElement && firstChild.type === TableBody;
    const isHead = isElement && firstChild.type === TableHead;
    return (_jsx("div", { ...attributes, className: rootClassNames, ref: rootRef, children: _jsx("table", { className: s.table, children: isBody || isHead ? children : _jsx(TableBody, { children: children }) }) }));
};
Table.Cell = TableCell;
Table.Heading = TableHeading;
Table.Row = TableRow;
Table.Body = TableBody;
Table.Head = TableHead;
Table.displayName = "Table";
TableCell.displayName = "TableCell";
TableHeading.displayName = "TableHeading";
TableRow.displayName = "TableRow";
TableBody.displayName = "TableBody";
TableHead.displayName = "TableHead";
export default Table;
